import * as React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useDataProvider } from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';

declare const __BUILD_TIME__: string;
declare const __BUILD_MODE__: string;
declare const __BUILD_COMMIT__: string;
declare const __BUILD_SOURCE__: string;

const BB_BASE = 'https://bitbucket.org/oppimisen-palvelut';

const CommitLink = ({ hash, repo }: { hash: string; repo: string }) => {
    if (!hash) return null;
    const short = hash.substring(0, 7);
    return (
        <Link
            href={`${BB_BASE}/${repo}/commits/${hash}`}
            target="_blank"
            rel="noopener"
            sx={{ color: 'inherit', textDecoration: 'underline' }}
        >
            {short}
        </Link>
    );
};

const BuildLine = ({ parts }: { parts: React.ReactNode[] }) => {
    const visible = parts.filter(part => part !== null && part !== '');
    return (
        <Typography
            variant="caption"
            sx={{
                color: 'inherit',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
            }}
        >
            {visible.map((part, i) => (
                <React.Fragment key={i}>
                    {i > 0 && ' | '}
                    {part}
                </React.Fragment>
            ))}
        </Typography>
    );
};

const useFrontendBuildInfo = () => {
    const buildTime =
        typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';
    const buildMode =
        typeof __BUILD_MODE__ !== 'undefined' ? __BUILD_MODE__ : '';
    const buildCommit =
        typeof __BUILD_COMMIT__ !== 'undefined' ? __BUILD_COMMIT__ : '';
    const buildSource =
        typeof __BUILD_SOURCE__ !== 'undefined' ? __BUILD_SOURCE__ : '';

    if (!buildTime) return null;

    const formatted = new Date(buildTime).toLocaleString('fi-FI', {
        timeZone: 'Europe/Helsinki',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    return [
        `Frontend: ${formatted}`,
        buildMode || null,
        buildCommit ? (
            <CommitLink key="commit" hash={buildCommit} repo="infra2023" />
        ) : null,
        buildSource || null,
    ];
};

const useBackendBuildInfo = () => {
    const [info, setInfo] = React.useState<any>(null);
    const dataProvider = useDataProvider();

    React.useEffect(() => {
        dataProvider
            .getAny('ping/detail', '')
            .then(({ data }) => setInfo(data))
            .catch(() => {});
    }, [dataProvider]);

    // still loading — nothing to show yet
    if (!info) return null;

    // version format: "20.11.0:commitHash" — env var is unset locally, giving literal "undefined"
    const rawHash = info.version?.split(':')?.[1] || '';
    const commitHash = rawHash === 'undefined' ? '' : rawHash;

    const buildTime = info.build_timestamp
        ? new Date(info.build_timestamp.replace('_', 'T') + 'Z').toLocaleString(
              'fi-FI',
              {
                  timeZone: 'Europe/Helsinki',
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
              }
          )
        : '—';

    const uptime = info.uptime
        ? `${Math.floor(info.uptime / 3600)}h ${Math.floor((info.uptime % 3600) / 60)}m`
        : '—';

    return [
        `Backend: ${buildTime}`,
        commitHash ? (
            <CommitLink
                key="commit"
                hash={commitHash}
                repo="oppimateriaalit-api"
            />
        ) : null,
        `uptime ${uptime}`,
        info.message || null,
    ];
};

const BuildInfoBar = () => {
    const { canAccess } = useCanAccess({
        action: 'debug',
        resource: 'application',
    });
    const frontend = useFrontendBuildInfo();
    const backend = useBackendBuildInfo();

    // hide until the backend ping has resolved (frontend info is always available at build time)
    if (!canAccess || !frontend || !backend) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                alignSelf: 'flex-end',
                opacity: 0.8,
                ml: 2,
                mr: 2,
                mb: 0.5,
                minWidth: 0,
                overflow: 'hidden',
            }}
        >
            <BuildLine parts={frontend} />
            <BuildLine parts={backend} />
        </Box>
    );
};

export default BuildInfoBar;
